import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { z } from 'zod';

import { FEATURE } from '~/common/enums/feature.enum';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import useIsUserAdmin from '~/domains/payments/hooks/useIsUserAdmin';
import {
  getLocationSettings,
  getContest,
  getContestTemplateById,
} from '~/services/contests/contests.service.api';
import { getLeagues } from '~/services/games-service/games-service.service.api';
import { contestsServiceInstance } from '~/domains/contest/domains/services/contest.service.instance';
import { BRANDS } from '~/domains/auth/consts';

import {
  getCreateSectionsData,
  getEditSectionsData,
  getInitialSectionsData,
  getManageSectionsData,
} from '../server/sections.data';
import { getTitleConfig } from '../domains/common/utils/getTitleConfig';
import { getSubmitConfig } from '../domains/common/utils/getSubmitConfig';
import type { QueryParams } from '../domains/common/api/settings/types/query.params';

const getRouterBrand = (brand: string) => {
  const BrandSchema = z.nativeEnum(BRANDS);
  const parsedBrand = BrandSchema.safeParse(brand);
  return parsedBrand.success ? parsedBrand.data : BRANDS.RYP;
};

const getClientSideManageContestData = async (contestId: string, brand: BRANDS) => {
  const response = await contestsServiceInstance.get(
    `/contest-metadata/${contestId}/restart-contest-data?brand=${brand}`
  );
  return response.data;
};

export const useContestCreateData = () => {
  const router = useRouter();
  const { leagueId, contestTemplateId } = router.query;
  const isUnifiedFlow = useIsFeatureEnabled(FEATURE.ENABLE_UNIFIED_CAC_FLOW);
  const isUserAdmin = useIsUserAdmin();

  // eslint-disable-next-line local-rules/require-query-stale-time
  return useQuery({
    queryKey: ['contest-create-data', leagueId, contestTemplateId, isUnifiedFlow, isUserAdmin],
    queryFn: async () => {
      if (!isUnifiedFlow) {
        return { isUnifiedFlow };
      }

      const locationSettings = await getLocationSettings();
      const allowedTagNames = locationSettings.allowedTagNames || [];

      if (!leagueId || !contestTemplateId) {
        const leagues = await getLeagues();
        const sectionsData = getInitialSectionsData(
          leagues,
          {
            leagueId: leagueId as string,
            contestTemplateId: contestTemplateId as string,
          },
          isUserAdmin
        );

        const titleConfig = getTitleConfig({ operation: 'create' });
        const submitConfig = getSubmitConfig({ operation: 'init' });

        return {
          allowedTagNames,
          sectionsData,
          titleConfig,
          submitConfig,
          isUnifiedFlow,
        };
      }

      const data: QueryParams = {
        leagueId: leagueId as string,
        contestTemplateId: contestTemplateId as string,
        allowedTagNames,
      };
      const contestTemplate = await getContestTemplateById(contestTemplateId as string).catch(
        () => undefined
      );

      const sectionsData = getCreateSectionsData(data, {
        description: contestTemplate?.settings?.description,
      });
      const titleConfig = getTitleConfig({ operation: 'create' });
      const submitConfig = getSubmitConfig({ operation: 'create' });

      return {
        allowedTagNames,
        sectionsData,
        data,
        titleConfig,
        submitConfig,
        isUnifiedFlow,
      };
    },
    enabled: router.isReady,
  });
};

export const useContestEditData = (contestId: string) => {
  const isUnifiedFlow = useIsFeatureEnabled(FEATURE.ENABLE_UNIFIED_CAC_FLOW);

  // eslint-disable-next-line local-rules/require-query-stale-time
  return useQuery({
    queryKey: ['contest-edit-data', contestId, isUnifiedFlow],
    queryFn: async () => {
      if (!isUnifiedFlow) {
        return { isUnifiedFlow };
      }

      try {
        const contest = await getContest(contestId);
        const locationSettings = await getLocationSettings();
        const allowedTagNames = locationSettings.allowedTagNames || [];

        const sectionsData = getEditSectionsData(
          {
            contestId: contest.id,
            contestTemplateId: contest.contest_template_id,
            leagueId: contest.league.id,
            allowedTagNames,
          },
          {
            name: contest.name,
            description: contest.description,
            banner_url: contest.banner_url,
          }
        );

        const titleConfig = getTitleConfig({ operation: 'edit' });
        const submitConfig = getSubmitConfig({ operation: 'edit' });

        return {
          sectionsData,
          contestId,
          titleConfig,
          submitConfig,
          isUnifiedFlow,
          commissioner: contest.commissioner,
        };
      } catch (error) {
        return { isUnifiedFlow };
      }
    },
    enabled: !!contestId,
  });
};

export const useContestRestartData = (contestId: string) => {
  const router = useRouter();
  const { brand: brandQuery } = router.query;
  const brand = getRouterBrand(brandQuery as string);
  const isUnifiedFlow = useIsFeatureEnabled(FEATURE.ENABLE_UNIFIED_CAC_FLOW);

  // eslint-disable-next-line local-rules/require-query-stale-time
  return useQuery({
    queryKey: ['contest-restart-data', contestId, brand, isUnifiedFlow],
    queryFn: async () => {
      if (!isUnifiedFlow) {
        return { isUnifiedFlow };
      }

      try {
        const [locationSettings, restartContestData] = await Promise.all([
          getLocationSettings(),
          getClientSideManageContestData(contestId, brand),
        ]);

        const allowedTagNames = locationSettings.allowedTagNames || [];

        const restartData = {
          leagueId: restartContestData?.leagueId,
          contestTemplateId: restartContestData?.contestTemplateId,
          needsEORestart: restartContestData?.needsEORestart,
          brand,
          contestName: restartContestData?.contestName,
          banner_url: restartContestData?.banner_url,
          ...restartContestData?.settings,
        };

        const sectionsData = getManageSectionsData(
          {
            ...restartData,
            allowedTagNames,
          },
          {
            name: restartData.contestName,
            banner_url: restartData.banner_url,
          }
        );

        const titleConfig = getTitleConfig({ operation: 'restart', brand });
        const submitConfig = getSubmitConfig({ operation: 'restart' });

        return {
          allowedTagNames,
          sectionsData,
          restartData,
          titleConfig,
          submitConfig,
          isUnifiedFlow,
          contestId,
        };
      } catch (error) {
        return { isUnifiedFlow };
      }
    },
    enabled: !!contestId && router.isReady,
  });
};
